import { Box, Button, HStack, Icon, Text, Tooltip, VStack } from '@chakra-ui/react';
import { TbArrowMerge, TbInfoCircle } from 'react-icons/tb';
import type { LocationEntry } from '../../../../hooks/useGetLocations';
import type { InsuranceSearchResult } from '../../../../hooks/useSearchInsurance';
import {
    SettingsCard,
    SettingsCheckTable,
    SettingsFlowSplit,
    SettingsRow,
} from '../../../../components/settings/primitives';
import CarrierSearchField from '../../../../components/settings/CarrierSearchField';
import SubCompanyTable, {
    type SubCompanyRow,
} from '../../../../components/settings/SubCompanyTable';
import type { DrpDraft, DrpSubMode } from './drpDraft';

/** Sentinel id for the "All Locations" row in the locations table. */
const ALL_LOCATIONS_ROW = '__all__';

export interface DrpCarrierStepProps {
    draft: DrpDraft;
    onChange: (next: DrpDraft) => void;
    isEditMode: boolean;
    locations: LocationEntry[];
    /** Other carriers in the selected carrier's group, and what happens to each. */
    subCompanyRows: SubCompanyRow[];
    /**
     * A group mate that is already a DRP partner with rules of its own, so this
     * carrier can be folded into it instead of keeping a second set.
     */
    mergeTarget?: { insurerId: string; insurerName: string };
    /** The group parent this carrier belongs under, when there is one. */
    parentLabel?: string;
    onSelectCarrier: (company: InsuranceSearchResult) => void;
    onClearCarrier: () => void;
    onJoinParent: (parent: { insurerId: string; insurerName: string }) => void;
    onCancelJoin: () => void;
}

/**
 * First step: which carrier, and where it applies. The carrier and the sub
 * companies that come with it are on the left; the locations it covers are on
 * the right, so the two halves of "who" and "where" are answered together.
 *
 * A carrier and its group are one set of rules, not one row each: sub companies
 * start on the carrier's settings and can be branched onto their own from here.
 * When a group mate is already configured, joining it is offered first — that is
 * almost always what a shop means when it adds a second name from one group.
 */
const DrpCarrierStep = ({
    draft,
    onChange,
    isEditMode,
    locations,
    subCompanyRows,
    mergeTarget,
    parentLabel,
    onSelectCarrier,
    onClearCarrier,
    onJoinParent,
    onCancelJoin,
}: DrpCarrierStepProps) => {
    const hasCarrier = draft.insurerName.trim().length > 0;
    const isJoining = !!draft.joinParentId;
    const allSelected =
        locations.length > 0 &&
        locations.every((location) => draft.locationIds.includes(location._id as string));

    const handleToggleLocation = (id: string, checked: boolean) => {
        if (id === ALL_LOCATIONS_ROW) {
            onChange({
                ...draft,
                locationIds: checked ? locations.map((location) => location._id as string) : [],
            });
            return;
        }
        onChange({
            ...draft,
            locationIds: checked
                ? Array.from(new Set([...draft.locationIds, id]))
                : draft.locationIds.filter((locationId) => locationId !== id),
        });
    };

    const handleSubModeChange = (insurerId: string, mode: DrpSubMode) => {
        onChange({ ...draft, subModes: { ...draft.subModes, [insurerId]: mode } });
    };

    const left = (
        // A flex column, not a scrolling box: the sub companies table takes
        // whatever height is left over and scrolls on its own, so it grows with
        // its rows instead of sitting in a fixed window.
        <VStack align="stretch" spacing={6} flex={1} minH={0} px={6} pb={6} overflow="hidden">
            {hasCarrier ? (
                <SettingsCard title="Carrier">
                    <SettingsRow
                        label={draft.insurerName}
                        // Only the sub-company relationship is worth a line
                        // here; the carrier's name speaks for itself.
                        description={
                            parentLabel
                                ? `Sub company of ${parentLabel}, with its own rules`
                                : undefined
                        }
                        control={
                            <HStack spacing={2}>
                                {/* Merging is offered next to the name because
                                    that is where the relationship is read: this
                                    carrier stops keeping rules of its own and
                                    follows the group mate that already does. */}
                                {mergeTarget && !isJoining && (
                                    <Tooltip
                                        label={`Follow ${mergeTarget.insurerName}'s settings instead of keeping rules here`}
                                        placement="top"
                                        hasArrow
                                        openDelay={300}
                                    >
                                        {/* Reads as one of the sub company
                                            table's actions, because it is the
                                            same action from the other side. */}
                                        <Button
                                            size="xs"
                                            variant="outline"
                                            colorScheme="gray"
                                            leftIcon={
                                                <Icon
                                                    as={TbArrowMerge as React.ElementType}
                                                    boxSize={3.5}
                                                />
                                            }
                                            onClick={() => onJoinParent(mergeTarget)}
                                        >
                                            Merge into {mergeTarget.insurerName}
                                        </Button>
                                    </Tooltip>
                                )}
                                {!isEditMode && (
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="gray"
                                        onClick={onClearCarrier}
                                    >
                                        Change
                                    </Button>
                                )}
                            </HStack>
                        }
                    />
                </SettingsCard>
            ) : (
                // Not a SettingsCard: the card clips its content, which would
                // cut off the search results hanging below the input.
                <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={1}>
                        Carrier
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={2.5}>
                        Search the insurers Yaxle knows and pick one.
                    </Text>
                    <CarrierSearchField onSelect={onSelectCarrier} autoFocus />
                    <HStack spacing={2} align="flex-start" mt={4}>
                        <Icon
                            as={TbInfoCircle as React.ElementType}
                            boxSize={4}
                            color="gray.400"
                            mt="2px"
                            flexShrink={0}
                        />
                        <Text fontSize="sm" color="gray.500">
                            Don't see the carrier you're looking for? If your insurer isn't listed,
                            contact us and we'll add them.
                        </Text>
                    </HStack>
                </Box>
            )}

            {/* Folded into a group mate: there is nothing left to configure, so
                the sub companies and locations panes step aside. */}
            {hasCarrier && isJoining && (
                <SettingsCard title="Settings">
                    <SettingsRow
                        label={`Follows ${draft.joinParentName}`}
                        description={`${draft.insurerName} is listed under ${draft.joinParentName} and takes its locations, price matching and vendor rules${isEditMode ? ', replacing the ones set here' : ''}. Saving applies it.`}
                        control={
                            <Button size="xs" variant="ghost" colorScheme="gray" onClick={onCancelJoin}>
                                Configure separately
                            </Button>
                        }
                    />
                </SettingsCard>
            )}

            {/* A sub company has no sub companies of its own: its group is run
                from the parent's screen, so the table would only offer to
                reshuffle a group this carrier does not head. */}
            {hasCarrier && !isJoining && !parentLabel && (
                <Box flex={1} minH={0} display="flex" flexDirection="column">
                    <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={1} flexShrink={0}>
                        Sub companies
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={2.5} flexShrink={0}>
                        Others in this carrier's group. Companies on {draft.insurerName}'s settings
                        share one entry in your DRP list; branch any that need their own rules.
                    </Text>
                    <SubCompanyTable
                        fill
                        parentName={draft.insurerName}
                        emptyMessage="No other companies in this group"
                        rows={subCompanyRows}
                        onModeChange={handleSubModeChange}
                    />
                </Box>
            )}
        </VStack>
    );

    return (
        <SettingsFlowSplit
            leftWidth="50%"
            leftTitle={isEditMode ? 'Carrier' : 'Add carrier'}
            left={left}
            isLeftFlush
            // The locations pane waits on a carrier, the same way the vendor
            // screen waits before offering preferences.
            rightTitle={hasCarrier && !isJoining ? 'Applicable locations' : undefined}
            rightDescription={
                hasCarrier && !isJoining
                    ? 'Which of your shops run work for this carrier.'
                    : undefined
            }
            right={
                hasCarrier && !isJoining ? (
                    <Box flex={1} minH={0} px={6} pb={6} display="flex" flexDirection="column">
                        <SettingsCheckTable
                            fill
                            columnLabel="LOCATION"
                            checkLabel="APPLIES"
                            emptyMessage="No locations configured yet"
                            rows={[
                                {
                                    id: ALL_LOCATIONS_ROW,
                                    label: 'All Locations',
                                    isChecked: allSelected,
                                    isEmphasis: true,
                                },
                                ...locations.map((location) => ({
                                    id: location._id as string,
                                    label: location.title,
                                    isChecked: draft.locationIds.includes(location._id as string),
                                })),
                            ]}
                            onToggle={handleToggleLocation}
                        />
                    </Box>
                ) : null
            }
            isRightFlush
        />
    );
};

export default DrpCarrierStep;
